import { apiClient } from './apiClient';
import {
  Campaign, CampaignBatch, CampaignBufferStatus,
  CreateCampaignRequest, UpdateCampaignRequest, GenerateBatchRequest,
} from '@agency-smm/shared';

interface CampaignListItem extends Campaign {
  workspaceCount: number;
  groupACount: number;
  groupBCount: number;
}

export const campaignService = {
  async list(): Promise<CampaignListItem[]> {
    return apiClient.get('/api/campaigns');
  },

  async get(id: string): Promise<Campaign & { batches: CampaignBatch[] }> {
    return apiClient.get(`/api/campaigns/${id}`);
  },

  async create(data: CreateCampaignRequest): Promise<{ id: string; name: string }> {
    return apiClient.post('/api/campaigns', data);
  },

  async update(id: string, data: UpdateCampaignRequest): Promise<{ success: boolean }> {
    return apiClient.put(`/api/campaigns/${id}`, data);
  },

  async delete(id: string): Promise<{ success: boolean }> {
    return apiClient.delete(`/api/campaigns/${id}`);
  },

  async addWorkspace(campaignId: string, workspaceId: string, groupLabel: 'A' | 'B') {
    return apiClient.post(`/api/campaigns/${campaignId}/workspaces`, { workspaceId, groupLabel });
  },

  async removeWorkspace(campaignId: string, workspaceId: string) {
    return apiClient.delete(`/api/campaigns/${campaignId}/workspaces/${workspaceId}`);
  },

  async updateWorkspaceGroup(campaignId: string, workspaceId: string, groupLabel: 'A' | 'B') {
    return apiClient.put(`/api/campaigns/${campaignId}/workspaces/${workspaceId}`, { groupLabel });
  },

  async getBufferStatus(campaignId: string): Promise<CampaignBufferStatus> {
    return apiClient.get(`/api/campaigns/${campaignId}/buffer-status`);
  },

  async generateBatch(campaignId: string, data: GenerateBatchRequest): Promise<{
    batchId: string; totalPosts: number; postsPerAccount: number; status: string;
    workspaces: { workspaceId: string; workspaceName: string }[];
  }> {
    return apiClient.post(`/api/campaigns/${campaignId}/generate-batch`, data);
  },

  async generateWorkspace(campaignId: string, batchId: string, workspaceId: string): Promise<{
    workspaceId: string; postsGenerated: number; totalBatchProgress: number; error: string | null;
  }> {
    return apiClient.post(`/api/campaigns/${campaignId}/batches/${batchId}/generate-workspace`, { workspaceId });
  },

  async completeBatch(campaignId: string, batchId: string, errors: string[]): Promise<{ status: string; generatedPosts: number }> {
    return apiClient.post(`/api/campaigns/${campaignId}/batches/${batchId}/complete`, { errors });
  },

  async listBatches(campaignId: string): Promise<CampaignBatch[]> {
    return apiClient.get(`/api/campaigns/${campaignId}/batches`);
  },

  async getBatch(campaignId: string, batchId: string): Promise<CampaignBatch> {
    return apiClient.get(`/api/campaigns/${campaignId}/batches/${batchId}`);
  },

  getExportUrl(campaignId: string, batchId: string): string {
    const base = (import.meta as any).env?.VITE_API_URL || 'https://agency-smm-api.carlos-mdt-1057.workers.dev';
    return `${base}/api/campaigns/${campaignId}/batches/${batchId}/export`;
  },
};
